import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { ISLANDS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Users, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", nif: "", phone: "", email: "", address: "", island: "", municipality: "", notes: "" };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { search: search || undefined } });
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) return toast.error("O nome é obrigatório");
    setBusy(true);
    try {
      const payload = { ...form, contacts: [] };
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Cliente atualizado" : "Cliente criado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminar o cliente "${c.name}"?`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminado");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="clients-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Gestão</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Clientes</h1>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid="new-client-btn">
          <Plus size={18} className="mr-1.5" /> Novo Cliente
        </Button>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Pesquisar por nome, NIF ou e-mail..." value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="clients-search" />
      </div>

      <div className="rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <Users size={40} className="text-muted-foreground" />
            <p className="text-muted-foreground">Ainda não há clientes.</p>
            <Button variant="outline" className="rounded-full" onClick={openNew}>Adicionar primeiro cliente</Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden md:table-cell">NIF</TableHead>
                <TableHead className="hidden lg:table-cell">Contacto</TableHead>
                <TableHead className="hidden md:table-cell">Ilha</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} className="stagger-item" data-testid={`client-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{c.nif || "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">{c.phone || c.email || "—"}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{c.island || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(c)}
                      data-testid={`delete-client-${c.id}`}>
                      <Trash size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Nome / Razão social *</Label>
              <Input value={form.name} onChange={set("name")} data-testid="client-name" />
            </div>
            <div className="space-y-1.5">
              <Label>NIF</Label>
              <Input value={form.nif} onChange={set("nif")} data-testid="client-nif" />
            </div>
            <div className="space-y-1.5">
              <Label>Telefone</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="client-phone" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>E-mail</Label>
              <Input type="email" value={form.email} onChange={set("email")} data-testid="client-email" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Morada</Label>
              <Input value={form.address} onChange={set("address")} data-testid="client-address" />
            </div>
            <div className="space-y-1.5">
              <Label>Ilha</Label>
              <Select value={form.island} onValueChange={(v) => setForm({ ...form, island: v })}>
                <SelectTrigger data-testid="client-island"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {ISLANDS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Concelho</Label>
              <Input value={form.municipality} onChange={set("municipality")} data-testid="client-municipality" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={set("notes")} data-testid="client-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-client-btn">
              {busy ? <CircleNotch className="animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
